<script setup lang="ts">
import type { ArgumentSearchHit } from "@/composables/useArgumentSearch"

defineProps<{
  hit: ArgumentSearchHit
  hideKeyPoint?: boolean
  sortedBy?: 'relevance' | 'quality'
  flatInfo?: boolean
}>()

const emit = defineEmits<{
  (e: 'navigate-to-keypoint', keyPoint: string, stance: string | undefined): void
}>()

const formatScore = (s: number | null | undefined) =>
  typeof s === 'number' && s > 0 ? s.toFixed(3) : '—'

const stripPrefix = (url: string) =>
  url.replace(/^https?:\/\/(www\.)?/, '')


const stanceClass = (stance?: string) => {
  if (!stance) return "bg-yellow-50 text-yellow-700 border-yellow-200"
  const s = stance.toLowerCase()
  if (s === "con")
    return "bg-[#3A91A5]/2 text-[#3A91A5] border-[#3A91A5]"
  if (s === "pro")
    return "bg-purple-30 text-purple-700 border-purple-600"
}
</script>

<template>
  <div class="rounded-2xl border border-gray-200 bg-white shadow-sm p-3 space-y-2">
    <!-- Argumentative Segments -->
    <div v-if="hit.matching_premises?.length || hit.top_premise" class="space-y-2">
      <div v-for="(p, idx) in hit.matching_premises || []" :key="idx" class="space-y-1">
        <div class="flex items-center justify-between gap-2">
          <span class="text-xs inline-block px-1 py-0.5 rounded-md border" :class="stanceClass(hit.stance)">
            Argumentative Segment
          </span>
          <div class="flex items-center gap-2 text-xs">
            <a v-if="hit.url" :href="hit.url" target="_blank" rel="noopener" class="text-blue-600 hover:underline">
              <span class="sm:hidden">{{ stripPrefix(hit.url).slice(0, 16) }}… ↗</span>
              <span class="hidden sm:inline">{{ stripPrefix(hit.url).slice(0, 18) }}… ↗</span>
            </a>
          </div>
        </div>

        <!-- Gray area now only wraps the content -->
        <div class="rounded-xl border border-gray-100 bg-gray-50 p-2">
          <p class="text-sm text-gray-500 whitespace-pre-wrap" v-html="p.highlight"></p>
        </div>

        <!-- Scores & Key Point table -->
        <div class="text-xs" :class="flatInfo ? '' : 'rounded-lg border border-gray-100 bg-gray-50'">
          <table class="w-full">
            <tbody>
              <tr :class="flatInfo ? '' : 'border-b border-gray-100'">
                <td class="px-1 py-0.5 text-gray-500 w-px whitespace-nowrap pr-3">Argument Quality</td>
                <td class="px-1 py-0.5 text-gray-500">{{ formatScore(hit.quality_score) }}</td>
              </tr>
              <tr :class="flatInfo ? '' : 'border-b border-gray-100'">
                <td class="px-1 py-0.5 text-gray-500 w-px whitespace-nowrap pr-3">Search Relevance</td>
                <td class="px-1 py-0.5 text-gray-500">{{ formatScore(hit.score) }}</td>
              </tr>
              <tr v-if="!hideKeyPoint">
                <td class="px-1 py-0.5 text-gray-500 w-px whitespace-nowrap pr-3 align-top">Key Point</td>
                <td class="px-1 py-0.5 text-gray-500">
                  <button
                    v-if="hit.key_point"
                    @click="emit('navigate-to-keypoint', hit.key_point, hit.stance)"
                    class="text-left text-gray-500 hover:underline"
                  >{{ hit.key_point }} ↗</button>
                  <span v-else>—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </div>


  </div>
</template>
